import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  ReferenceLine,
  Brush
} from 'recharts';
import { useAuth } from '../../../../providers/AuthContext';
import Spinner from '../../../../components/Spinner';
import { getWidgetById } from '../../../../services/apiWidgests';
import useWidgetRuntime from '../../../../hooks/useWidgetRuntime';
import { useDashboards } from '../../../../providers/DashboardContext';
import DatabaseOffIco from '../../../../components/icons/DatabaseOffIco';
import { useAppContext } from '../../../../providers/AppContext';

const CustomTooltip = ({ active, payload }) => {
  if (active && payload?.length) {
    const { value, payload: item } = payload[0];
    const formattedValue =
      Number(value) % 1 === 0 ? Number(value) : Number(value).toFixed(2);
    return (
      <div className="bg-black text-white px-2 py-1 rounded opacity-90">
        <div className="text-sm">{item.label}</div>
        <div className="font-semibold">{formattedValue}</div>
      </div>
    );
  }
  return null;
};

const CustomXAxisTick = ({ x, y, payload }) => {
  const parseCustomDate = (dateStr) => {
    // Espera el formato: "dd/mm/yyyy - HH:MM:SS"
    const [datePart, timePart] = dateStr.split('-');

    return [datePart, timePart];
  };

  const [datePart, timePart] = parseCustomDate(payload.value);


  return (
    <g transform={`translate(${x},${y})`}>
      <text x={0} y={0} dy={16} textAnchor="middle" fill="#666">
        {datePart}
      </text>
      <text x={0} y={20} dy={16} textAnchor="middle" fill="#666">
        {timePart}
      </text>
    </g>
  );
};

const CustomLabel = ({ x, y, value, width, height, index }) => {
  const formattedValue =
    Number(value) % 1 === 0 ? Number(value) : Number(value).toFixed(2);

  // Asegurar que el texto no se corte
  const safeY = y < 20 ? y + 20 : y - 10;

  return (
    <text
      x={x}
      y={safeY}
      fill="#374151"
      fontSize={12}
      textAnchor="middle"
    >
      {formattedValue}
    </text>
  );
};

// Mapea el estilo de línea elegido en el form a un strokeDasharray de recharts.
const dashArrayFor = (lineStyle) => {
  if (lineStyle === 'dashed') return '6 4';
  if (lineStyle === 'dotted') return '2 4';
  return undefined; // sólido
};

// Etiqueta de un marcador, dibujada como un "tag"/badge con fondo de color
// en vez de texto plano (que casi no se veía sobre la grilla). `position`
// decide si el badge queda pegado a la izquierda, centrado, o a la derecha
// a lo largo de la línea de referencia. `viewBox` lo pasa recharts: cubre
// todo el ancho graficado y su `y` es el pixel exacto de la línea.
const MarkerBadge = ({ viewBox, text, color = '#ef4444', position = 'center' }) => {
  if (!text || !viewBox) return null;
  const { x, width, y } = viewBox;
  const paddingX = 6;
  const badgeWidth = Math.max(28, text.length * 6.4 + paddingX * 2);
  const badgeHeight = 16;

  let badgeX = x + width / 2 - badgeWidth / 2; // center
  if (position === 'left') badgeX = x + 2;
  if (position === 'right') badgeX = x + width - badgeWidth - 2;

  const badgeY = y - badgeHeight - 4;

  return (
    <g>
      <rect x={badgeX} y={badgeY} width={badgeWidth} height={badgeHeight} rx={4} fill={color} />
      <text
        x={badgeX + badgeWidth / 2}
        y={badgeY + badgeHeight / 2 + 4}
        textAnchor="middle"
        fontSize={11}
        fontWeight={600}
        fill="#ffffff"
      >
        {text}
      </text>
    </g>
  );
};

const TelemetryLineChartWidget = ({ id }) => {
  const { auth } = useAuth();
  const navigate = useNavigate();
  const {dashboard, selectedDatarange} = useDashboards();
  const { data: runtimeData, error: runtimeError, loading: fetchingWidgetData } = useWidgetRuntime(id, dashboard?._id);
  const data = runtimeData || [];
  const [fetchingWidgetInfo, setFetchingWidgetInfo] = useState(true);
  const [variableLabel, setVariableLabel] = useState("");
  const [timeLabel, setTimeLabel] = useState("");
  const [style, setStyle] = useState({});
  const [brushRange, setBrushRange] = useState(null); // {startIndex, endIndex} - controlado a mano para que el zoom no se pierda en cada refresco
  const { addToast } = useAppContext();

  const refreshWidgetInfo = async () => {
    try {
      setFetchingWidgetInfo(true);
      const response = await getWidgetById(id, auth?.token);
      setVariableLabel(
        response?.configuration?.variable?.alias ||
        response?.configuration?.variable?.name ||
        ""
      );
      setStyle(response?.configuration?.style || {});

      setFetchingWidgetInfo(false);
    } catch (error) {
      if (!error || !error.response) {
        addToast("No se pudo completar la consulta. Inténtalo nuevamente", 2);
        setFetchingWidgetInfo(false);
        return;
      }
      if (error.response?.status === 404) {
        console.log("Widget no encontrado");
      } else if (error.response?.status === 500) {
        console.log("Error en el servidor, intente nuevamente");
      } else {
        navigate('/logout');
      }
      setFetchingWidgetInfo(false);
    }
  };

  // Los datos ya no se piden una vez por REST: useWidgetRuntime los recibe
  // por socket desde el singleton compartido del backend (se actualizan
  // solos cada vez que el runtime hace tick, sin volver a pedirlos acá).
  useEffect(() => {
    if (runtimeError) {
      addToast("No se pudo completar la consulta. Inténtalo nuevamente", 2);
    }
  }, [runtimeError]);

  useEffect(() => {
    refreshWidgetInfo();
  }, [selectedDatarange]);

  return (
    <div className="w-full h-full p-4 bg-white border border-gray-300 rounded">
      {(fetchingWidgetData  || fetchingWidgetInfo)? (
        <div className='w-full h-full flex justify-center'>
          <Spinner />
        </div>
      ) : (
        <>
          <div className="mb-4 flex items-center justify-center">
            <div className="w-4 h-4 mr-2 rounded-sm" style={{ backgroundColor: '#3b82f6' }}></div>
            <span className="text-gray-700">
              {variableLabel} vs tiempo
            </span>
          </div>
          {data.length === 0 && (
            <div className='w-full h-full flex flex-col items-center justify-center -mt-8'>
              <span className='text-lg text-gray-700'>No hay datos por el momento</span>
              <DatabaseOffIco width='40px' height='40px'  fill='gray'/>
            </div>

          )}
          {data.length > 0 && (
            <ResponsiveContainer width="100%" height="80%">
              <LineChart
                data={data}
                margin={{ top: 30, right: 20, bottom: 20, left: 20 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="label" stroke="#6b7280"  tick={<CustomXAxisTick />}/>
                <YAxis stroke="#6b7280" />
                <Tooltip content={<CustomTooltip />} />
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke={style.lineColor || "#3b82f6"}
                  strokeWidth={style.lineWidth || 2}
                  strokeDasharray={dashArrayFor(style.lineStyle)}
                  dot={style.showDots ? { r: 3, fill: style.lineColor || "#3b82f6" } : false}
                  activeDot={{ r: 6 }}
                  isAnimationActive={false}

                />
                {style.zoomEnabled && data.length > 5 && (
                  <Brush
                    dataKey="label"
                    height={20}
                    stroke={style.lineColor || "#3b82f6"}
                    travellerWidth={8}
                    startIndex={brushRange ? Math.min(brushRange.startIndex, data.length - 1) : undefined}
                    endIndex={brushRange ? Math.min(brushRange.endIndex, data.length - 1) : undefined}
                    onChange={(range) => setBrushRange(range)}
                  />
                )}
                {/* Los marcadores van AL FINAL a propósito: recharts pinta los
                    elementos hijos en orden, así que lo último en el JSX queda
                    dibujado por ENCIMA de todo lo anterior (línea, grilla,
                    brush). Antes estaban primero y la línea/grilla los tapaba. */}
                {(style.yMarkers || []).map((marker) => (
                  <ReferenceLine
                    key={marker.id}
                    y={marker.value}
                    stroke={marker.color || '#ef4444'}
                    strokeDasharray={dashArrayFor(marker.style)}
                    label={
                      marker.label
                        ? <MarkerBadge text={marker.label} color={marker.color} position={marker.labelPosition} />
                        : undefined
                    }
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          )}
        </>
      )}
    </div>
  );
};

export default TelemetryLineChartWidget;
